<template>
    <button
        type="button"
        :class="[
            full ? 'w-full' : '',
            hideText ? 'w-10 h-10' : 'h-10 px-4'
        ]"
        :disabled="loading"
        class="facebook-login-button inline-flex items-center justify-center gap-2 bg-[#1877F2] rounded-full transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md hover:bg-[#166FE5]"
        @click="loginWithFacebook"
    >
        <div class="flex items-center justify-center gap-2">
            <!-- Facebook Logo -->
            <div class="flex-shrink-0" style="width: 20px; height: 20px;">
                <svg v-if="!loading" width="20" height="20" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <svg v-else class="animate-spin text-white" style="width: 20px; height: 20px;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
            <span v-if="!hideText" class="whitespace-nowrap font-medium">
                {{ createAccount ? $t('auth.signUpWithFacebook') : $t('auth.signInWithFacebook') }}
            </span>
        </div>
    </button>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { FacebookAuthProvider, signInWithPopup } from 'firebase/auth'

import { CButtonSizes } from '@/types/buttons'


interface Props {
    createAccount?: boolean
    hideText?: boolean
    full?: boolean
    size?: string
}

const props = withDefaults(defineProps<Props>(), {
    createAccount: false,
    hideText: false,
    full: false,
    size: CButtonSizes.Normal
})

const emit = defineEmits(['success', 'error'])

const { $firebase, $auth } = useNuxtApp()
const loading = ref(false)

async function loginWithFacebook() {
    try {
        loading.value = true
        const provider = new FacebookAuthProvider()

        // Add scopes if needed
        // provider.addScope('user_birthday')

        const result = await signInWithPopup($auth, provider)

        // This gives you a Facebook Access Token. You can use it to access the Facebook API.
        // const credential = FacebookAuthProvider.credentialFromResult(result)
        // const token = credential?.accessToken

        // The signed-in user info
        const user = result.user
        emit('success', user)
    } catch (error: any) {
        console.error('Facebook login error:', error)
        // Handle Errors here.
        // const errorCode = error.code
        // const errorMessage = error.message
        // The email of the user's account used.
        // const email = error.customData.email
        // The AuthCredential type that was used.
        // const credential = FacebookAuthProvider.credentialFromError(error)
        emit('error', error)
    } finally {
        loading.value = false
    }
}
</script>

<style scoped>
.facebook-login-button {
    -webkit-appearance: none;
    background-image: none;
    border: 1px solid #1877F2;
    box-sizing: border-box;
    color: #ffffff;
    cursor: pointer;
    /* Roboto via the theme variable: naming 'Roboto' here made @nuxt/fonts inline its @font-face into every page */
    font-family: var(--font-family-sans);
    font-size: 14px;
    letter-spacing: 0.25px;
    outline: none;
    overflow: hidden;
    position: relative;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
}

.facebook-login-button:not(:disabled):hover {
    box-shadow: 0 1px 2px 0 rgba(60, 64, 67, .30), 0 1px 3px 1px rgba(60, 64, 67, .15);
}
</style>
